import { Capacitor, registerPlugin } from '@capacitor/core'

import {
  isNativeExternalOpenAvailable,
  openNativeExternalUrl,
} from './nativeExternalOpen'

const APK_MIME = 'application/vnd.android.package-archive'
const APK_DOWNLOAD_PATHS = new Set([
  '/apk/install',
  '/apk/latest',
  '/api/apk/latest',
])

type NativeAppInfo = { packageName?: string; versionCode: number | string; versionName?: string }
type AgentChatAppInfoPlugin = { getInfo(): Promise<NativeAppInfo> }
const AgentChatAppInfo = registerPlugin<AgentChatAppInfoPlugin>('AgentChatAppInfo')

/**
 * In-app APK self-updater bridge (native AgentChatUpdaterPlugin). Present only
 * in native shells built after the updater landed; older shells and browser
 * tabs fall back to the legacy WebView-download path in
 * openAgentChatApkInstaller().
 */
export type ApkInstallStatus =
  | 'installer_opened' // APK downloaded in-app and Android's installer launched
  | 'external_opened' // bootstrap path: opened the APK download outside the old native shell
  | 'permission_required' // opened "install unknown apps" settings; grant then retry
  | 'in_progress' // a download is already running; this tap was coalesced
  | 'navigated' // legacy/browser path: navigated to the installer URL

export type ApkDownloadProgress = {
  /** 0–100, or null when the server sent no Content-Length. */
  percent: number | null
  receivedBytes: number
  totalBytes: number | null
}

const APK_INSTALL_STATUSES = new Set<ApkInstallStatus>([
  'installer_opened',
  'external_opened',
  'permission_required',
  'in_progress',
  'navigated',
])

type AgentChatUpdaterPlugin = {
  installUpdate(options: { url: string }): Promise<{ status?: unknown }>
  addListener(
    eventName: 'downloadProgress',
    listenerFunc: (data: { percent: number; receivedBytes: number; totalBytes: number }) => void,
  ): Promise<{ remove: () => void | Promise<void> }>
}
const AgentChatUpdater = registerPlugin<AgentChatUpdaterPlugin>('AgentChatUpdater')

/** Coalesces concurrent/rapid taps so a flurry can't queue duplicate installs. */
let apkInstallInFlight = false
const LEGACY_INSTALL_COOLDOWN_MS = 20_000
const PLUGIN_INSTALL_COOLDOWN_MS = 1_500
// First APK whose native AgentChatUpdater stages the APK through Android's
// PackageInstaller session API and resolves success only after Android returns
// a user-confirmation intent or install success. Older shells can load the
// current web bundle, but their native updater can falsely report success, so
// they must bootstrap through Android's external download path.
const RELIABLE_NATIVE_UPDATER_MIN_VERSION_CODE = 1_782_605_000

export type ApkUpdateInfo = {
  installedVersionCode: number | null
  installedVersionName: string | null
  latestVersionCode: number | null
  latestVersionName: string | null
  /** true only when we can prove latest > installed. */
  updateAvailable: boolean
  /** true when the server has a published APK but we couldn't compare versions. */
  latestAvailable: boolean
}

function uaApkVersionCode(): number | null {
  const m = /AgentChatApk\/(\d+)/.exec(navigator.userAgent || '')
  const n = m ? Number(m[1]) : NaN
  return Number.isFinite(n) && n > 0 ? n : null
}

function uaApkVersionName(): string | null {
  const m = /AgentChatApk\/\d+\s*\(([^)]+)\)/.exec(navigator.userAgent || '')
  return m ? m[1].trim() : null
}

/**
 * Installed native APK version. Prefers the AgentChatAppInfo Capacitor plugin
 * (exact), falls back to parsing the custom `AgentChatApk/<code>` User-Agent
 * suffix the native shell appends — so it still works on older shells that
 * predate the plugin.
 */
export async function getInstalledApkVersion(): Promise<{ code: number | null; name: string | null }> {
  try {
    if (Capacitor.isPluginAvailable('AgentChatAppInfo')) {
      const info = await AgentChatAppInfo.getInfo()
      const code = Number(info.versionCode)
      if (Number.isFinite(code) && code > 0) {
        return { code, name: info.versionName ? String(info.versionName) : uaApkVersionName() }
      }
    }
  } catch {
    /* fall through to the User-Agent heuristic */
  }
  return { code: uaApkVersionCode(), name: uaApkVersionName() }
}

type LatestApkVersion = {
  versionCode?: number | null
  versionName?: string | null
}

/**
 * Compare the installed native APK against the published build. Never throws —
 * on any failure it degrades to "latest available, comparison unknown" so the
 * caller can still offer a plain install rather than a dead end.
 */
export async function fetchApkUpdateInfo(): Promise<ApkUpdateInfo> {
  const installed = await getInstalledApkVersion()

  let latestVersionCode: number | null = null
  let latestVersionName: string | null = null
  let latestAvailable = false
  try {
    const r = await fetch('/api/apk/latest-version', { credentials: 'include', cache: 'no-store' })
    if (r.ok) {
      latestAvailable = true
      const j = (await r.json()) as LatestApkVersion
      const code = Number(j.versionCode)
      latestVersionCode = Number.isFinite(code) && code > 0 ? code : null
      latestVersionName = typeof j.versionName === 'string' ? j.versionName : null
    }
  } catch {
    /* offline / not built — leave latest unknown */
  }

  const updateAvailable =
    installed.code != null && latestVersionCode != null && latestVersionCode > installed.code

  return {
    installedVersionCode: installed.code,
    installedVersionName: installed.name,
    latestVersionCode,
    latestVersionName,
    updateAvailable,
    latestAvailable,
  }
}

export function agentChatApkDownloadUrl(cacheBust: string | null = null): string {
  const url = new URL('/api/apk/latest', window.location.origin)
  url.searchParams.set('agentchat_update', cacheBust || String(Date.now()))
  return url.toString()
}

export function agentChatApkInstallUrl(cacheBust: string | null = null): string {
  const url = new URL('/apk/install', window.location.origin)
  url.searchParams.set('agentchat_update', cacheBust || String(Date.now()))
  return url.toString()
}

export function canUseNativeApkUpdater(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AgentChatUpdater')
}

async function canUseReliableNativeApkUpdater(): Promise<boolean> {
  if (!canUseNativeApkUpdater()) return false
  const installed = await getInstalledApkVersion()
  return (
    installed.code != null &&
    installed.code >= RELIABLE_NATIVE_UPDATER_MIN_VERSION_CODE
  )
}

function requireKnownApkInstallStatus(status: unknown): ApkInstallStatus {
  if (typeof status === 'string' && APK_INSTALL_STATUSES.has(status as ApkInstallStatus)) {
    return status as ApkInstallStatus
  }
  const label = status == null ? 'missing' : JSON.stringify(status)
  throw new Error(`Native updater did not confirm installer launch (${label} status).`)
}

/**
 * Kick off an APK update.
 *
 * Native shells with the AgentChatUpdater plugin download the APK in-app — one
 * download, real progress via `onProgress`, real success/failure — and launch
 * Android's installer through FileProvider. Native shells that predate the
 * updater plugin use the already-installed external-open bridge when possible,
 * so Android/Chrome owns the bootstrap download instead of the stale WebView
 * self-install path. Plain browser tabs navigate to the stable installer URL.
 * A module-level guard coalesces rapid repeat taps on every path, which is
 * what stops the old "tap 7x, downloads land together, installers stack"
 * failure.
 */
export async function openAgentChatApkInstaller(
  cacheBust: string | null = null,
  onProgress?: (progress: ApkDownloadProgress) => void,
): Promise<ApkInstallStatus> {
  if (apkInstallInFlight) return 'in_progress'
  apkInstallInFlight = true

  const nativeUpdaterAvailable = await canUseReliableNativeApkUpdater()

  if (!nativeUpdaterAvailable) {
    const releaseInFlightGuard = () => {
      apkInstallInFlight = false
    }

    if (isNativeExternalOpenAvailable()) {
      try {
        await openNativeExternalUrl({
          url: agentChatApkDownloadUrl(cacheBust),
          mimeType: APK_MIME,
          filename: 'agentchat.apk',
          chooserTitle: 'Install AgentChat update',
          fallbackUrl: agentChatApkInstallUrl(cacheBust),
        })
        // Android owns the bootstrap download/install from here. Hold the
        // guard briefly because there is no completion callback from the
        // external activity.
        window.setTimeout(releaseInFlightGuard, LEGACY_INSTALL_COOLDOWN_MS)
        return 'external_opened'
      } catch {
        // If Android has no external handler, keep the older browser/WebView
        // route available so the update card is still actionable.
      }
    }

    // Browsers and very old native shells: no in-app installer and no external
    // bootstrap bridge. Navigate to the stable installer URL. There's no
    // completion signal here, so hold the guard on a timer to coalesce rapid
    // repeat taps, then auto-recover.
    window.setTimeout(() => {
      apkInstallInFlight = false
    }, LEGACY_INSTALL_COOLDOWN_MS)
    window.location.assign(agentChatApkInstallUrl(cacheBust))
    return 'navigated'
  }

  let progressHandle: { remove: () => void | Promise<void> } | null = null
  try {
    if (onProgress) {
      progressHandle = await AgentChatUpdater.addListener('downloadProgress', (data) => {
        onProgress({
          percent: data.percent >= 0 ? data.percent : null,
          receivedBytes: data.receivedBytes,
          totalBytes: data.totalBytes > 0 ? data.totalBytes : null,
        })
      })
    }
    const result = await AgentChatUpdater.installUpdate({ url: agentChatApkDownloadUrl(cacheBust) })
    const status = requireKnownApkInstallStatus(result?.status)
    // Success: brief cooldown so a stray double-tap can't kick a 2nd download.
    window.setTimeout(() => {
      apkInstallInFlight = false
    }, PLUGIN_INSTALL_COOLDOWN_MS)
    return status
  } catch (err) {
    // Real failure — let the user retry immediately.
    apkInstallInFlight = false
    throw err
  } finally {
    if (progressHandle) {
      try {
        await progressHandle.remove()
      } catch {
        /* listener teardown is best-effort */
      }
    }
  }
}

export function isAgentChatApkDownloadUrl(url: URL): boolean {
  return url.origin === window.location.origin && APK_DOWNLOAD_PATHS.has(url.pathname)
}
